import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import CollapsibleBoxComponent from '../../../components/CollapsibleBox/CollapsibleBoxComponent';
import TextFieldComponent from '../../../components/TextField/TextFieldComponent';
import DropdownComponent from '../../../components/DropDown/DropDownComponent';
import AddressBarComponent from '../../../components/AddressBar/AddressBarComponent';
import LoadingDots from '../../../components/LoadingDots/LoadingDots';
import { withMinDuration } from '../../../utils/withMinDuration';
import VehicleSearchComponent from '../../../components/VehicleSearch/VehicleSearchComponent';
import vehicleTemplate from '../../../json/components/subscreens/VehicleTemplate.json';
import {
  profileAddVehicle,
  profileDeleteVehicle,
} from '../../../api/apiServices';
import { normalizeParkingType } from '../../../utils/apiHelpers';
import { normalizeCellNumber } from '../../../utils/questionEngineOtpHelpers';
import useValidationRegistry from '../useValidationRegistry';

const componentMap = {
  'text-field': TextFieldComponent,
  dropdown: DropdownComponent,
  'address-bar': AddressBarComponent,
};

const getFreshVehicle = () => JSON.parse(JSON.stringify(vehicleTemplate));

const getVehicleSearchDescription = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.SearchDescription || value.VehicleDescription || '';
};

const getSelectedVehicle = (value) => {
  if (!value || typeof value !== 'object') return null;
  return value;
};

const buildAddVehiclePayload = (vehicle = {}, policyHolderCellNumber = '') => {
  const selectedVehicle = getSelectedVehicle(vehicle?.makeModel?.value);
  const makeModel = String(
    getVehicleSearchDescription(selectedVehicle) ||
      vehicle?.makeModel?.value ||
      ''
  ).trim();
  const [vehicleMake, ...vehicleModelParts] = makeModel.split(/\s+/);
  const vehicleModel = vehicleModelParts.join(' ').trim();

  // normalizeCellNumber rewrites a leading "27" to "0", which would corrupt
  // an email-based identifier (e.g. "27smith@gmail.com") — only normalize
  // when the resolved identifier is actually a cell number.
  const policyHolderIdentifier = String(policyHolderCellNumber || '').includes(
    '@'
  )
    ? String(policyHolderCellNumber).trim()
    : normalizeCellNumber(policyHolderCellNumber);

  return {
    vehicleMake: vehicleMake || makeModel || '',
    vehicleModel: vehicleModel || makeModel || '',
    vehicleKey: selectedVehicle?.VehicleKey ?? vehicle?.VehicleKey ?? '',
    vehicleYear: vehicle?.year?.value || '',
    policyHolderCellphoneNumber: policyHolderIdentifier,
    vehicleDescription:
      vehicle?.description?.value ||
      getVehicleSearchDescription(selectedVehicle) ||
      '',
    nightTimeAddress: vehicle?.nightAddress?.value || '',
    nightTimeSecuritySetup: vehicle?.nightSecurity?.value || '',
    dayTimeAddress: vehicle?.dayAddress?.value || '',
    dayTimeSecuritySetup: vehicle?.daySecurity?.value || '',
    carUse: vehicle?.usage?.value || '',
  };
};

const mapVehicleToTemplate = (vehicle = {}) => {
  const nextVehicle = getFreshVehicle();
  nextVehicle.VehiclePK = vehicle?.VehiclePK ?? null;

  nextVehicle.year.value =
    vehicle?.VehicleYear != null
      ? String(vehicle.VehicleYear)
      : vehicle?.vehicleYear != null
        ? String(vehicle.vehicleYear)
        : '';
  nextVehicle.makeModel.value =
    [vehicle?.VehicleMake, vehicle?.VehicleModel].filter(Boolean).join(' ') ||
    vehicle?.VehicleDescription ||
    vehicle?.vehicleDescription ||
    '';
  nextVehicle.description.value =
    vehicle?.VehicleDescription || vehicle?.vehicleDescription || '';
  nextVehicle.nightAddress.value =
    vehicle?.NightTimeAddress || vehicle?.nightTimeAddress || '';
  nextVehicle.nightSecurity.value = normalizeParkingType(
    vehicle?.NightTimeSecuritySetup || vehicle?.nightTimeSecuritySetup
  );
  nextVehicle.dayAddress.value =
    vehicle?.DayTimeAddress || vehicle?.dayTimeAddress || '';
  nextVehicle.daySecurity.value = normalizeParkingType(
    vehicle?.DayTimeSecuritySetup || vehicle?.dayTimeSecuritySetup
  );
  nextVehicle.usage.value =
    vehicle?.CarUse || vehicle?.VehicleUse || vehicle?.carUse || '';

  return nextVehicle;
};

// Pre-live: the per-vehicle Reset action is temporarily disabled. Flip to true
// to restore it post-live.
const ALLOW_VEHICLE_RESET = false;

const VehicleDetailsContent = ({
  isMobile,
  savedVehiclesData = [],
  onResetVehicles,
  policyHolderIdentifier = '',
}) => {
  const { registerValidation, validateAll } = useValidationRegistry();
  const [savedVehicles, setSavedVehicles] = useState([]);
  const [showNewVehicle, setShowNewVehicle] = useState(false);
  const [newVehicle, setNewVehicle] = useState(getFreshVehicle());
  const [isSavingNewVehicle, setIsSavingNewVehicle] = useState(false);
  const [deletingVehicleIndices, setDeletingVehicleIndices] = useState(
    () => new Set()
  );
  const [resettingVehicleIndices, setResettingVehicleIndices] = useState(
    () => new Set()
  );

  useEffect(() => {
    setSavedVehicles(
      Array.isArray(savedVehiclesData)
        ? savedVehiclesData.map((vehicle) => mapVehicleToTemplate(vehicle))
        : []
    );
  }, [savedVehiclesData]);

  const handleNewChange = (fieldName, value) => {
    setNewVehicle((prev) => {
      const next = { ...prev };
      if (next[fieldName]) {
        next[fieldName] = {
          ...next[fieldName],
          value,
        };
      }

      if (fieldName === 'year' && prev.year?.value !== value) {
        if (next.makeModel) {
          next.makeModel = { ...next.makeModel, value: '' };
        }
        if (next.description) {
          next.description = { ...next.description, value: '' };
        }
        next.VehicleKey = undefined;
      }

      if (fieldName === 'makeModel') {
        const selectedVehicle = getSelectedVehicle(value);
        const description = getVehicleSearchDescription(selectedVehicle);
        if (next.description) {
          next.description = {
            ...next.description,
            value: description,
          };
        }

        if (selectedVehicle?.VehicleKey !== undefined) {
          next.VehicleKey = selectedVehicle.VehicleKey;
        }
      }

      return next;
    });
  };

  const handleSavedChange = (index, fieldName, value) => {
    const updated = [...savedVehicles];
    updated[index][fieldName].value = value;
    setSavedVehicles(updated);
  };

  const deleteSavedVehicle = async (index) => {
    const vehicle = savedVehicles[index];
    const vehiclePK = vehicle?.VehiclePK;

    setDeletingVehicleIndices((prev) => new Set(prev).add(index));
    try {
      await withMinDuration(
        (async () => {
          if (vehiclePK) {
            await profileDeleteVehicle(vehiclePK);
          }
          await onResetVehicles?.(undefined, {
            showLoader: false,
            forceRefresh: true,
          });
        })()
      );
    } catch (error) {
      // ignore
    } finally {
      setDeletingVehicleIndices((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  const resetSavedVehicle = async (index) => {
    setResettingVehicleIndices((prev) => new Set(prev).add(index));
    try {
      await withMinDuration(
        onResetVehicles?.(undefined, {
          showLoader: false,
          forceRefresh: true,
        })
      );
    } catch (error) {
      // ignore
    } finally {
      setResettingVehicleIndices((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  const resetNewVehicle = () => {
    setNewVehicle(getFreshVehicle());
  };

  const saveNewVehicle = async () => {
    if (!validateAll()) {
      return;
    }

    setIsSavingNewVehicle(true);
    try {
      await withMinDuration(
        (async () => {
          await profileAddVehicle(
            buildAddVehiclePayload(newVehicle, policyHolderIdentifier)
          );
          await onResetVehicles?.(undefined, {
            showLoader: false,
            forceRefresh: true,
          });
        })()
      );
      setNewVehicle(getFreshVehicle());
      setShowNewVehicle(false);
    } catch (error) {
      // ignore
    } finally {
      setIsSavingNewVehicle(false);
    }
  };

  const renderVehicleFields = (
    vehicle,
    onChange,
    index = null,
    registerFieldValidation = null,
    isReadOnly = false
  ) => {
    const entries = Object.entries(vehicle);

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 416px)',
          columnGap: isMobile ? 0 : '32px',
          rowGap: isMobile ? 4 : '16px',
          justifyContent: 'center',
          alignItems: 'start',
        }}
      >
        {entries.map(([fieldKey, config]) => {
          if (!config || typeof config !== 'object' || !config.type) {
            return null;
          }

          const Component = componentMap[config.type];
          if (!Component) return null;
          const isFullWidth = config.customSize === 'accounts-full';

          const isNewVehicleSearch = index === null && fieldKey === 'makeModel';

          return (
            <Box
              key={fieldKey}
              sx={{
                width: '100%',
                gridColumn: !isMobile && isFullWidth ? '1 / -1' : 'auto',
              }}
            >
              {isNewVehicleSearch ? (
                <VehicleSearchComponent
                  name={fieldKey}
                  question={config}
                  value={config.value}
                  vehicleYear={newVehicle.year?.value || ''}
                  isMobile={isMobile}
                  layoutVariant="account"
                  registerValidation={registerFieldValidation}
                  onChange={(name, value) => onChange(name, value)}
                />
              ) : (
                <Component
                  name={fieldKey}
                  question={config}
                  value={config.value}
                  onChange={(name, value) =>
                    index === null
                      ? onChange(name, value)
                      : onChange(index, name, value)
                  }
                  registerValidation={registerFieldValidation}
                  isMobile={isMobile}
                  layoutVariant="account"
                  readOnly={isReadOnly}
                />
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      {/* Existing Vehicles */}
      {savedVehicles.map((vehicle, index) => (
        <CollapsibleBoxComponent
          key={index}
          title={vehicle.makeModel?.value || `Vehicle ${index + 1}`}
          isMobile={isMobile}
          defaultExpanded={false}
        >
          <Box sx={{ px: isMobile ? 1.5 : 2 }}>
            {renderVehicleFields(vehicle, handleSavedChange, index, null, true)}

            {ALLOW_VEHICLE_RESET ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  mt: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'stretch',
                }}
              >
                <Button
                  color="inherit"
                  onClick={() => resetSavedVehicle(index)}
                  disabled={resettingVehicleIndices.has(index)}
                  sx={{
                    backgroundColor: '#E2E5EA',
                    px: 4,
                    py: 1.5,
                    borderRadius: '20px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: isMobile ? '0.8125rem' : '1.25rem',
                    color: '#1C2F3A',
                    textTransform: 'none',
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { sm: '120px' },
                  }}
                >
                  {resettingVehicleIndices.has(index) ? (
                    <LoadingDots />
                  ) : (
                    'Reset'
                  )}
                </Button>
              </Box>
            ) : null}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                onClick={() => deleteSavedVehicle(index)}
                disabled={deletingVehicleIndices.has(index)}
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.625rem',
                  fontWeight: 500,
                  color: '#FF3434',
                  borderBottom: deletingVehicleIndices.has(index)
                    ? 'none'
                    : '1px solid #FF3434',
                  borderRadius: 0,
                  padding: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#FF3434',
                    borderBottom: deletingVehicleIndices.has(index)
                      ? 'none'
                      : '2px solid #FF3434',
                  },
                }}
              >
                {deletingVehicleIndices.has(index) ? (
                  <LoadingDots />
                ) : (
                  'Delete this Vehicle'
                )}
              </Button>
            </Box>
          </Box>
        </CollapsibleBoxComponent>
      ))}

      {/* Add Another Vehicle Button */}
      {!showNewVehicle && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
          }}
        >
          <Button
            onClick={() => setShowNewVehicle(true)}
            sx={{
              height: '40px',
              backgroundColor: '#FF0074',
              borderRadius: '16px',
              px: 2,
              py: 1,
              gap: '12px',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '1rem',
              lineHeight: '1rem',
              letterSpacing: '-0.002em',
              fontFamily: 'BInter, Inter, sans-serif',
              textTransform: 'none',
              boxShadow: 'none',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: '#e10068',
              },
            }}
          >
            Add Vehicle +
          </Button>
        </Box>
      )}

      {/* New Vehicle Form */}
      {showNewVehicle && (
        <CollapsibleBoxComponent
          title="New vehicle"
          isMobile={isMobile}
          defaultExpanded={true}
        >
          <Box sx={{ px: isMobile ? 1.5 : 2 }}>
            {renderVehicleFields(
              newVehicle,
              handleNewChange,
              null,
              registerValidation
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                mt: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
              }}
            >
              <Button
                color="inherit"
                onClick={() => {
                  resetNewVehicle();
                  setShowNewVehicle(false);
                }}
                sx={{
                  backgroundColor: '#E2E5EA',
                  px: 4,
                  py: 1.5,
                  borderRadius: '20px',
                  fontWeight: 600,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: isMobile ? '0.8125rem' : '1.25rem',
                  color: '#1C2F3A',
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={saveNewVehicle}
                disabled={isSavingNewVehicle}
                sx={{
                  backgroundColor: '#C7CA00',
                  px: 4,
                  py: 1.5,
                  borderRadius: '20px',
                  fontWeight: 600,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: isMobile ? '0.8125rem' : '1.25rem',
                  color: '#1C2F3A',
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: '205px' },
                }}
              >
                {isSavingNewVehicle ? <LoadingDots /> : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </CollapsibleBoxComponent>
      )}
    </Box>
  );
};

export default VehicleDetailsContent;
